import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// Animated Counter Component
const AnimatedCounter: React.FC<{ end: number, duration?: number, prefix?: string, suffix?: string }> = ({
  end,
  duration = 2200,
  prefix = '',
  suffix = ''
}) => {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    let startTime: number | null = null;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const current = Math.floor(progress * end);
      node.textContent = prefix + current.toLocaleString() + suffix;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        node.textContent = prefix + end.toLocaleString() + suffix;
      }
    };

    requestAnimationFrame(animate);
  }, [end, duration, prefix, suffix]);

  return <span ref={ref}>{prefix}0{suffix}</span>;
};

const Hakkimizda: React.FC = () => {
  return (
    <div>
      {/* Page Hero */}
      <section style={{
        background: 'linear-gradient(135deg, #0066CC 0%, #003D82 100%)',
        color: '#fff',
        padding: '4rem 0',
        textAlign: 'center',
      }}>
        <div style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '0 1.5rem',
        }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 4vw, 2.75rem)',
            fontWeight: 700,
            marginBottom: '0.75rem',
            letterSpacing: '-0.02em',
          }}>
            Hakkımızda
          </h1>
          <p style={{
            fontSize: '1.125rem',
            opacity: 0.9,
            lineHeight: 1.6,
          }}>
            Motopack, uzman ekibiyle kurumsal müşterilerine güvenilir kurye çözümleri sunar.
          </p>
        </div>
      </section>

      {/* About Content */}
      <section style={{
        padding: '4rem 0',
        background: '#fff',
      }}>
        <div style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '0 1.5rem',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '3rem',
            alignItems: 'center',
          }}>
            {/* Text */}
            <div>
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: 700,
                color: '#1e293b',
                marginBottom: '1rem',
                letterSpacing: '-0.02em',
              }}>
                Biz Kimiz?
              </h2>
              <p style={{
                fontSize: '1rem',
                color: '#64748b',
                lineHeight: 1.7,
              }}>
                Motopack, 2020 yılından bu yana işletmelerin kurye ve lojistik ihtiyaçlarına yenilikçi ve güvenilir çözümler sunan bir teknoloji girişimidir. Kurumsal müşterilerimize özel geliştirdiğimiz sistemlerle gönderi süreçlerini daha verimli, hızlı ve izlenebilir hale getirirken; operasyonel yükleri azaltır, maliyetleri optimize eder ve riskleri minimuma indiririz.
              </p>
            </div>

            {/* Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1rem',
            }}>
              {[
                { value: 500000, label: 'Teslimat', prefix: '+' },
                { value: 250, label: 'Mutlu Müşteri', prefix: '+' },
                { value: 100, label: 'Çalışan', prefix: '+' },
              ].map((stat, index) => (
                <div key={index} style={{
                  textAlign: 'center',
                  padding: '1.25rem',
                  background: '#f8fafc',
                  borderRadius: 12,
                  border: '1px solid #e2e8f0',
                }}>
                  <div style={{
                    fontSize: '1.75rem',
                    fontWeight: 800,
                    color: '#0066CC',
                    marginBottom: '0.25rem',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    <AnimatedCounter end={stat.value} prefix={stat.prefix} duration={2500} />
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    color: '#64748b',
                    fontWeight: 500,
                  }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section style={{
        padding: '4rem 0',
        background: '#f8fafc',
      }}>
        <div style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '0 1.5rem',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
          }}>
            {/* Mission */}
            <div style={{
              background: '#fff',
              borderRadius: 14,
              padding: '2rem',
              borderTop: '3px solid #0066CC',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: '#1e293b',
                marginBottom: '0.75rem',
              }}>
                Misyonumuz
              </h3>
              <p style={{
                fontSize: '0.9375rem',
                color: '#64748b',
                lineHeight: 1.7,
              }}>
                Türkiye'nin dört bir yanında, işletmelere ve bireylere en hızlı, güvenilir ve yenilikçi kurye hizmetini sunmak; teknolojiyi ve insan odağını birleştirerek lojistikte fark yaratmak.
              </p>
            </div>

            {/* Vision */}
            <div style={{
              background: '#fff',
              borderRadius: 14,
              padding: '2rem',
              borderTop: '3px solid #0066CC',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: '#1e293b',
                marginBottom: '0.75rem',
              }}>
                Vizyonumuz
              </h3>
              <p style={{
                fontSize: '0.9375rem',
                color: '#64748b',
                lineHeight: 1.7,
              }}>
                Motopack'i, Türkiye'nin en çok tercih edilen ve en güvenilir kurye ağı haline getirmek; sürdürülebilir, çevreci ve akıllı lojistik çözümleriyle sektöre öncülük etmek.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section style={{
        padding: '4rem 0',
        background: '#fff',
      }}>
        <div style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '0 1.5rem',
        }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: '1.75rem',
            fontWeight: 700,
            color: '#1e293b',
            marginBottom: '2.5rem',
            letterSpacing: '-0.02em',
          }}>
            Değerlerimiz
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1.5rem',
          }}>
            {[
              {
                icon: '/icon/hakkimizda/hız.png',
                title: 'Hız',
                description: 'Teslimatlarımızda hız ve dakiklik en büyük önceliğimizdir. Müşterilerimizin zamanına değer veririz.',
              },
              {
                icon: '/icon/hakkimizda/güven.png',
                title: 'Güvenilirlik',
                description: 'Her teslimatta güven ve şeffaflık sunarız. Gönderileriniz bize emanet, içiniz rahat olsun.',
              },
              {
                icon: '/icon/hakkimizda/teknoloji.png',
                title: 'Teknoloji',
                description: 'Operasyonlarımızı en güncel teknolojilerle yönetir, süreçlerimizi sürekli geliştiririz.',
              },
              {
                icon: '/icon/hakkimizda/memnuniyet.png',
                title: 'Müşteri Odaklılık',
                description: 'Her zaman müşterilerimizin ihtiyaçlarını ön planda tutar, onların memnuniyeti için çalışırız.',
              },
              {
                icon: '/icon/hakkimizda/sürdürebilirlik.png',
                title: 'Sürdürülebilirlik',
                description: 'Çevreye duyarlı ve sürdürülebilir lojistik çözümler geliştirir, topluma ve doğaya değer katarız.',
              },
              {
                icon: '/icon/hakkimizda/İnovasyon.png',
                title: 'Takım Ruhu',
                description: 'Birlikte büyür, birlikte başarırız. Motopack ekibi olarak her zaman dayanışma ve iş birliği içindeyiz.',
              },
            ].map((value, index) => (
              <div
                key={index}
                style={{
                  background: '#fff',
                  borderRadius: 14,
                  padding: '1.75rem',
                  border: '1px solid #e2e8f0',
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.08)';
                  e.currentTarget.style.borderColor = '#0066CC';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              >
                <img
                  src={value.icon}
                  alt={value.title}
                  style={{
                    width: 48,
                    height: 48,
                    marginBottom: '1rem',
                  }}
                />
                <h4 style={{
                  fontSize: '1.0625rem',
                  fontWeight: 600,
                  color: '#1e293b',
                  marginBottom: '0.5rem',
                }}>
                  {value.title}
                </h4>
                <p style={{
                  fontSize: '0.9375rem',
                  color: '#64748b',
                  lineHeight: 1.6,
                }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        background: 'linear-gradient(135deg, #0066CC 0%, #003D82 100%)',
        padding: '4rem 0',
        textAlign: 'center',
      }}>
        <div style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '0 1.5rem',
        }}>
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: 700,
            color: '#fff',
            marginBottom: '0.75rem',
          }}>
            Hızlı Teklif Alın
          </h2>
          <p style={{
            fontSize: '1rem',
            color: 'rgba(255,255,255,0.9)',
            marginBottom: '1.5rem',
          }}>
            Kurye ve lojistik ihtiyaçlarınız için hemen bizimle iletişime geçin, size özel çözümler sunalım.
          </p>
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            <Link
              to="/hizmetal"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0.875rem 1.75rem',
                background: '#fff',
                color: '#0066CC',
                fontWeight: 600,
                fontSize: '1rem',
                borderRadius: 10,
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }}
            >
              Teklif Alın
            </Link>
            <Link
              to="/iletisim"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0.875rem 1.75rem',
                background: 'transparent',
                color: '#fff',
                fontWeight: 600,
                fontSize: '1rem',
                borderRadius: 10,
                border: '1.5px solid rgba(255,255,255,0.5)',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.borderColor = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
              }}
            >
              Bilgi Alın
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hakkimizda;